/**
 * Token Refresh Utility
 * Handles automatic refresh of Cognito tokens before they expire.
 * Works for both Cognito (username/password) and Midway (OAuth) login flows.
 */

import { getTimeUntilExpiration } from "./tokenUtils";
import CONFIG from "../config";

const MIN_REFRESH_INTERVAL = 30000; // Don't refresh more than once per 30s
let lastRefreshTime = 0;
let refreshPromise = null; // Prevent concurrent refreshes

/**
 * Refresh tokens using the Cognito OAuth2 token endpoint.
 * Uses the refresh_token grant type which works for both Cognito and Midway users.
 * @returns {Promise<{idToken: string, accessToken: string}|null>}
 */
export async function refreshTokens() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    console.log("Token refresh: No refresh token available");
    return null;
  }

  // Prevent concurrent refresh calls
  if (refreshPromise) {
    return refreshPromise;
  }

  // Rate limit
  const now = Date.now();
  if (now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
    console.log("Token refresh: Rate limited, skipping");
    return null;
  }

  refreshPromise = _doRefresh(refreshToken);
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function _doRefresh(refreshToken) {
  try {
    console.log("Token refresh: Refreshing tokens...");
    lastRefreshTime = Date.now();

    const tokenEndpoint = `https://${CONFIG.cognitoDomain}/oauth2/token`;
    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CONFIG.userPoolClientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Token refresh failed:", response.status, errorData);
      return null;
    }

    const tokens = await response.json();

    // Update stored tokens (refresh_token is not returned on refresh grant)
    localStorage.setItem("idToken", tokens.id_token);
    localStorage.setItem("accessToken", tokens.access_token);

    console.log("Token refresh: Success, new token expires in", getTimeUntilExpiration(tokens.id_token), "seconds");
    return {
      idToken: tokens.id_token,
      accessToken: tokens.access_token,
    };
  } catch (error) {
    console.error("Token refresh: Error:", error);
    return null;
  }
}
